import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail, Send, Search, AlertCircle, CheckCircle2, Clock, ArrowLeft, RefreshCw, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const KIND_LABELS = {
  signup_welcome: "Welcome",
  job_digest: "Job digest",
  application_receipt: "Application receipt",
  password_reset: "Password reset",
  subscription_receipt: "Subscription receipt",
  recruiter_new_application: "Recruiter · new application",
  recruiter_candidate_match: "Recruiter · candidate match",
  admin_weekly_stats: "Admin weekly stats",
  admin_crawler_failure: "Admin · crawler failure",
};

const STATUS_BADGE = {
  sent: "bg-emerald-500/10 text-emerald-700",
  delivered: "bg-emerald-500/15 text-emerald-700",
  opened: "bg-emerald-500/20 text-emerald-800",
  clicked: "bg-emerald-500/25 text-emerald-800",
  queued: "bg-amber-500/10 text-amber-700",
  failed: "bg-red-500/10 text-red-700",
  bounced: "bg-red-500/15 text-red-800",
  complained: "bg-red-500/15 text-red-800",
};

export default function AdminEmailPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [emailQ, setEmailQ] = useState("");
  const [expanded, setExpanded] = useState(null);

  // Test send
  const [testTo, setTestTo] = useState("");
  const [testKind, setTestKind] = useState("signup_welcome");
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    if (!user || user.role !== "admin") return;
    try {
      const params = { limit: 200 };
      if (kind !== "all") params.kind = kind;
      if (status !== "all") params.status = status;
      if (emailQ) params.email = emailQ;
      const r = await api.get("/admin/email/audit", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setStats(r.data.stats || null);
    } catch {
      toast.error("Failed to load audit trail");
    }
  }, [kind, status, emailQ, user]);

  useEffect(() => { load(); }, [load]);

  const sendTest = async () => {
    if (!testTo) { toast.error("Enter a recipient email"); return; }
    setTesting(true);
    try {
      const r = await api.post("/admin/email/test", { to_email: testTo, kind: testKind });
      const s = r.data.status;
      if (s === "sent") toast.success(`Sent ✓ Resend id: ${r.data.resend_email_id?.slice(0, 12)}…`);
      else toast.error(`${s}: ${r.data.error || "see audit row"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setTesting(false); }
  };

  const runDigest = async (cadence) => {
    try {
      const r = await api.post(`/admin/digest/run-now?cadence=${cadence}`);
      toast.success(`Digest run · sent=${r.data.sent}, failed=${r.data.failed}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Digest failed");
    }
  };

  if (!user || user.role !== "admin") {
    return (
      <div className="container py-20 max-w-xl text-center" data-testid="admin-email-locked">
        <h1 className="display-2 mb-4">Admin only</h1>
      </div>
    );
  }

  return (
    <div className="container py-10">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="admin-email-back">
        <ArrowLeft className="h-4 w-4" /> Back to Admin Console
      </Link>
      <p className="eyebrow mb-1 inline-flex items-center gap-2"><Mail className="h-3 w-3" /> Email audit trail</p>
      <h1 className="display-2 mb-2">{total.toLocaleString()} emails</h1>
      <p className="text-sm text-muted-foreground mb-6">
        {stats?.resend_enabled
          ? <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-3 w-3" /> Resend live · all sends go to inboxes</span>
          : <span className="inline-flex items-center gap-1 text-amber-600"><AlertCircle className="h-3 w-3" /> DRY-RUN · audit is logged but no emails are actually sent</span>}
      </p>

      <Tabs defaultValue="trail">
        <TabsList className="grid w-full md:w-auto md:inline-grid grid-cols-3 mb-6" data-testid="admin-email-tabs">
          <TabsTrigger value="trail" data-testid="email-tab-trail">Audit trail</TabsTrigger>
          <TabsTrigger value="test" data-testid="email-tab-test">Send a test</TabsTrigger>
          <TabsTrigger value="ops" data-testid="email-tab-ops">Digest controls</TabsTrigger>
        </TabsList>

        <TabsContent value="trail">
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="email-stats">
              {(stats.by_status || []).map((s) => (
                <div key={s.status} className="vital-card-static p-4">
                  <div className="eyebrow">{s.status}</div>
                  <div className={`text-2xl font-black mt-1 ${s.status === "failed" || s.status === "bounced" ? "text-red-600" : ""}`}>{s.count}</div>
                </div>
              ))}
            </div>
          )}

          <div className="vital-card-static p-3 mb-4 grid md:grid-cols-[1fr_180px_180px] gap-2" data-testid="email-filters">
            <div className="flex items-center gap-2 px-3 border border-border rounded-lg bg-background">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input value={emailQ} onChange={(e) => setEmailQ(e.target.value)} placeholder="Search recipient email…" className="border-0 focus-visible:ring-0 shadow-none" data-testid="email-search" />
            </div>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="email-kind-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All kinds</SelectItem>
                {Object.entries(KIND_LABELS).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="email-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["all", "sent", "queued", "failed", "delivered", "bounced", "complained", "opened", "clicked"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto rounded-lg border border-border" data-testid="email-audit-table">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
                  <th className="py-2 px-3">When</th>
                  <th className="py-2 px-3">Kind</th>
                  <th className="py-2 px-3">To</th>
                  <th className="py-2 px-3">Subject</th>
                  <th className="py-2 px-3">Actor</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3 w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((row) => (
                  <React.Fragment key={row.audit_id}>
                    <tr className="hover:bg-muted/30 cursor-pointer" onClick={() => setExpanded(expanded === row.audit_id ? null : row.audit_id)} data-testid={`email-row-${row.audit_id}`}>
                      <td className="py-2 px-3 text-xs text-muted-foreground whitespace-nowrap">{new Date(row.created_at).toLocaleString()}</td>
                      <td className="py-2 px-3"><span className="pill">{KIND_LABELS[row.kind] || row.kind}</span></td>
                      <td className="py-2 px-3 font-mono text-xs">{row.audience_email}</td>
                      <td className="py-2 px-3 truncate max-w-xs">{row.subject}</td>
                      <td className="py-2 px-3 text-xs text-muted-foreground">{row.audit_actor}</td>
                      <td className="py-2 px-3"><span className={`text-[11px] px-2 py-0.5 rounded font-semibold ${STATUS_BADGE[row.status] || "bg-muted"}`}>{row.status}</span></td>
                      <td className="py-2 px-3"><ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${expanded === row.audit_id ? "rotate-180" : ""}`} /></td>
                    </tr>
                    {expanded === row.audit_id && (
                      <tr data-testid={`email-row-detail-${row.audit_id}`}>
                        <td colSpan={7} className="py-3 px-6 bg-muted/20 text-xs">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div><div className="eyebrow">audit_id</div><div className="font-mono">{row.audit_id}</div></div>
                            <div><div className="eyebrow">resend_email_id</div><div className="font-mono truncate">{row.resend_email_id || "—"}</div></div>
                            <div><div className="eyebrow">sent_at</div><div>{row.sent_at ? new Date(row.sent_at).toLocaleString() : "—"}</div></div>
                            <div><div className="eyebrow">idempotency</div><div className="font-mono truncate">{row.idempotency_key}</div></div>
                          </div>
                          <div className="mt-3"><div className="eyebrow mb-1">snippet</div><div className="font-mono text-[11px] whitespace-pre-wrap text-muted-foreground">{row.snippet}</div></div>
                          {row.error && <div className="mt-3"><div className="eyebrow mb-1 text-red-600">error</div><div className="font-mono text-[11px] text-red-700 whitespace-pre-wrap">{row.error}</div></div>}
                          {row.related_doc && <div className="mt-3"><div className="eyebrow mb-1">related</div><div className="font-mono">{row.related_doc.type} · {row.related_doc.id}</div></div>}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {items.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-muted-foreground" data-testid="email-empty">No emails match the filter.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="test">
          <div className="vital-card-static p-6 max-w-xl">
            <h3 className="font-bold mb-1">Send a test email</h3>
            <p className="text-sm text-muted-foreground mb-4">Useful for verifying your Resend domain + templates render correctly. Every test is also logged in the audit trail.</p>
            <div className="space-y-3">
              <div>
                <Label>Recipient email</Label>
                <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@yourdomain.com" data-testid="test-email-to" />
              </div>
              <div>
                <Label>Template</Label>
                <Select value={testKind} onValueChange={setTestKind}>
                  <SelectTrigger data-testid="test-email-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="signup_welcome">Signup welcome</SelectItem>
                    <SelectItem value="job_digest">Job digest (5 latest)</SelectItem>
                    <SelectItem value="application_receipt">Application receipt</SelectItem>
                    <SelectItem value="admin_weekly_stats">Admin weekly stats</SelectItem>
                    <SelectItem value="admin_crawler_failure">Admin crawler failure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={sendTest} disabled={testing || !testTo} className="w-full" data-testid="test-email-send">
                <Send className="mr-2 h-4 w-4" /> {testing ? "Sending…" : "Send test"}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="ops">
          <div className="vital-card-static p-6 max-w-xl">
            <h3 className="font-bold mb-1 inline-flex items-center gap-2"><Clock className="h-4 w-4" /> Digest controls</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Daily and weekly digests run automatically at 08:00 IST. Use these for a one-off manual run.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => runDigest("daily")} variant="outline" data-testid="run-digest-daily"><RefreshCw className="mr-2 h-4 w-4" />Run daily digest now</Button>
              <Button onClick={() => runDigest("weekly")} variant="outline" data-testid="run-digest-weekly"><RefreshCw className="mr-2 h-4 w-4" />Run weekly digest now</Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
